// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\staff\pages\CreateMerchant.tsx

import React, { useEffect, useState } from "react";
import StaffLayout from "../components/StaffLayout";

type JmcAssignee = {
  name: string;
  email: string;
};

type MerchantContact = {
  name: string;
  email: string;
};









function formatJapanesePhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}



export default function CreateMerchant() {
  // ✅ 候補（DBから動的に取得）
  const [presetJmcAssignees, setPresetJmcAssignees] = useState<JmcAssignee[]>([
    { name: "安成", email: "" },
    { name: "山下", email: "" },
    { name: "吉松", email: "" },
    { name: "谷岡", email: "" },
  ]);

  // ✅ Staff-only: 候補をDBから取得（自動で増える）
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch("/api/staff/merchants/jmc-candidates");
        const data = await res.json().catch(() => ({}));

                if (!res.ok) return;

        // ✅ Prefer new format: assignees [{name,email}]
        if (data?.assignees && Array.isArray(data.assignees)) {
          const list: JmcAssignee[] = (data.assignees as any[])
            .map((a) => ({
              name: String(a?.name ?? "").trim(),
              email: String(a?.email ?? "").trim(),
            }))
            .filter((a) => a.name.length > 0);

          if (!cancelled && list.length > 0) {
            setPresetJmcAssignees(list);
          }
          return;
        }

        // ✅ Backward fallback: names only
        if (data?.names && Array.isArray(data.names)) {
          const names: string[] = (data.names as any[])
            .map((n) => String(n ?? "").trim())
            .filter((n) => n.length > 0);

          const uniq: string[] = Array.from(new Set<string>(names));

          if (!cancelled && uniq.length > 0) {
            const next: JmcAssignee[] = uniq.map((n) => ({ name: n, email: "" }));
            setPresetJmcAssignees(next);
          }
        }

      } catch {
        // ignore (fallback to initial 4 names)
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const [form, setForm] = useState({
    companyName: "",
    companyNameKana: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    // ✅ NEW
    registrationDate: "",
    cuppyOnly: false,
    // ✅ MUST match backend ALLOWED_BUSINESS_TYPES
    industry: "美容医療", // 美容医療 / エステティック / 異業種 / BtoB / その他
    industryOther: "",
    notes: "",
  });

  // ✅ 加盟店担当者（追加分：複数）
  const [merchantContacts, setMerchantContacts] = useState<MerchantContact[]>([
    { name: "", email: "" },
  ]);

  // ✅ JMC担当者（複数）
  const [jmcAssignees, setJmcAssignees] = useState<JmcAssignee[]>([{ name: "", email: "" }]);


    // ✅ Auto-fill JMC担当者（1行目）をログイン中スタッフのGoogle情報で初期入力
  useEffect(() => {
    const staffName = (localStorage.getItem("staff_name") || "").trim();
    const staffEmail = (localStorage.getItem("staff_email") || "").trim();

    if (!staffName && !staffEmail) return;

    setJmcAssignees((prev) => {
      // If no rows, create first row
      if (!prev || prev.length === 0) {
        return [{ name: staffName, email: staffEmail }];
      }

      // Do NOT override if user already typed something
      const first = prev[0];
      const firstName = (first?.name || "").trim();
      const firstEmail = (first?.email || "").trim();

      if (firstName || firstEmail) return prev;

      const next = [...prev];
      next[0] = { ...next[0], name: staffName || "", email: staffEmail || "" };
      return next;
    });
  }, []);


  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{
  merchant_id: string;
  temp_password: string;
  email_sent?: boolean;
  email_error?: string;
} | null>(null);
  const [apiError, setApiError] = useState<string>("");

  const onChange = (key: keyof typeof form, value: string | boolean) => {
  setForm((prev) => ({ ...prev, [key]: value }));
};

  // -----------------------
  // Merchant contacts helpers
  // -----------------------
  const setMerchantContact = (idx: number, key: keyof MerchantContact, value: string) => {
    setMerchantContacts((prev) => prev.map((a, i) => (i === idx ? { ...a, [key]: value } : a)));
  };

  const addMerchantContact = () => {
    setMerchantContacts((prev) => [...prev, { name: "", email: "" }]);
  };

  const removeMerchantContact = (idx: number) => {
    setMerchantContacts((prev) => prev.filter((_, i) => i !== idx));
  };

  // -----------------------
  // JMC helpers
  // -----------------------
  const setAssignee = (idx: number, key: keyof JmcAssignee, value: string) => {
    setJmcAssignees((prev) => prev.map((a, i) => (i === idx ? { ...a, [key]: value } : a)));
  };

  const addAssignee = () => {
    setJmcAssignees((prev) => [...prev, { name: "", email: "" }]);
  };

  const removeAssignee = (idx: number) => {
    setJmcAssignees((prev) => prev.filter((_, i) => i !== idx));
  };

  const validateEmail = (email: string) => {
    const v = (email || "").trim();
    if (!v) return false;
    return v.includes("@");
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    // Existing validations
    if (!form.companyName.trim()) nextErrors.companyName = "会社名は必須です。";
    if (!form.contactName.trim()) nextErrors.contactName = "担当者名は必須です。";
    if (!form.contactEmail.trim()) nextErrors.contactEmail = "担当者メールは必須です。";

    if (form.contactEmail.trim() && !form.contactEmail.includes("@")) {
      nextErrors.contactEmail = "メールアドレスの形式が正しくありません。";
    }

    if (!form.industry.trim()) nextErrors.industry = "業種は必須です。";

    // ✅ 異業種 / その他 は自由入力必須
    if ((form.industry === "異業種" || form.industry === "その他") && !form.industryOther.trim()) {
      nextErrors.industryOther = "業種の内容を入力してください。";
    }

    // ✅ Merchant contacts validations (optional rows)
    const cleanedContacts = merchantContacts
      .map((c) => ({ name: (c.name || "").trim(), email: (c.email || "").trim() }))
      .filter((c) => c.name || c.email);

    cleanedContacts.forEach((c, idx) => {
      if (!c.name) nextErrors[`merchantContacts_${idx}_name`] = "担当者名は必須です。";
      if (!c.email) nextErrors[`merchantContacts_${idx}_email`] = "メールは必須です。";
      if (c.email && !validateEmail(c.email)) {
        nextErrors[`merchantContacts_${idx}_email`] = "メール形式が正しくありません。";
      }
    });

    // Duplicate check (include main contact + extra contacts)
    const allEmails = [
      form.contactEmail.trim().toLowerCase(),
      ...cleanedContacts.map((c) => c.email.toLowerCase()),
    ].filter((e) => e);

    const dupEmail = allEmails.find((e, i) => allEmails.indexOf(e) !== i);
    if (dupEmail) {
      nextErrors.merchantContacts = "加盟店担当者のメールが重複しています。";
    }

    // ✅ JMC担当者 validations
    const cleanedJmc = jmcAssignees
      .map((a) => ({ name: (a.name || "").trim(), email: (a.email || "").trim() }))
      .filter((a) => a.name || a.email); // ignore fully empty rows

    if (cleanedJmc.length === 0) {
      nextErrors.jmcAssignees = "JMC担当者を1名以上登録してください。";
    } else {
      cleanedJmc.forEach((a, idx) => {
        if (!a.name) nextErrors[`jmcAssignees_${idx}_name`] = "担当者名は必須です。";
        if (!a.email) nextErrors[`jmcAssignees_${idx}_email`] = "メールは必須です。";
        if (a.email && !validateEmail(a.email)) {
          nextErrors[`jmcAssignees_${idx}_email`] = "メール形式が正しくありません。";
        }
      });

      // Duplicate email check inside JMC
      const emails = cleanedJmc.map((a) => a.email.toLowerCase());
      const dup = emails.find((e, i) => emails.indexOf(e) !== i);
      if (dup) nextErrors.jmcAssignees = "JMC担当者のメールが重複しています。";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreate = async () => {
    const ok = validate();
    if (!ok) return;

    setSaving(true);
    setApiError("");
    setResult(null);

    const cleanedJmc = jmcAssignees
      .map((a) => ({ name: (a.name || "").trim(), email: (a.email || "").trim() }))
      .filter((a) => a.name || a.email);

    const cleanedContacts = merchantContacts
      .map((c) => ({ name: (c.name || "").trim(), email: (c.email || "").trim() }))
      .filter((c) => c.name || c.email);

    try {
      const res = await fetch("/api/staff/merchants/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: form.companyName,
          companyNameKana: form.companyNameKana,

          industry: form.industry,
          industryOther: form.industryOther,

          // backend ignores businessType now, but keeping is OK
          businessType: form.industry,

          contactName: form.contactName,
          contactEmail: form.contactEmail,
          contactPhone: form.contactPhone,
          notes: form.notes,

          // ✅ NEW
          registrationDate: form.registrationDate,
cuppyOnly: form.cuppyOnly,
merchantContacts: cleanedContacts,

          // ✅ existing
          jmcAssignees: cleanedJmc,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setApiError(data?.detail || "保存に失敗しました。入力内容を確認してください。");
        setSaving(false);
        return;
      }

      setResult({
  merchant_id: data.merchant_id,
  temp_password: data.temp_password,
  email_sent: data.email_sent,
  email_error: data.email_error,
});
      setSaving(false);
    } catch (e) {
      setApiError("保存に失敗しました。サーバーが起動しているか確認してください。");
      setSaving(false);
    }
  };

  const resetAll = () => {
    setForm({
      companyName: "",
      companyNameKana: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      registrationDate: "",
      cuppyOnly: false,
      industry: "美容医療",
      industryOther: "",
      notes: "",
    });

    setMerchantContacts([{ name: "", email: "" }]);
    setJmcAssignees([{ name: "", email: "" }]);
    setErrors({});
    setResult(null);
    setApiError("");
  };

  return (
    <StaffLayout title="加盟店アカウント作成" active="createMerchant">
      <div style={{ fontWeight: 900, marginBottom: 10 }}>基本情報入力（仮）</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="会社名（必須・全角）">
  <input
    value={form.companyName}
    onChange={(e) => onChange("companyName", e.target.value)}
    placeholder="例：株式会社〇〇（全角）"
    style={inputStyle}
  />
  <ErrorText msg={errors.companyName} />
</Field>

        <Field label="会社名（カナ・半角）">
  <input
    value={form.companyNameKana}
    onChange={(e) => onChange("companyNameKana", e.target.value)}
    placeholder="例：ｶﾌﾞｼｷｶﾞｲｼｬ○○（半角ｶﾅ）"
    style={inputStyle}
  />
</Field>

        {/* ✅ NEW 登録日 */}
        <Field label="登録日">
          <input
            type="date"
            value={form.registrationDate}
            onChange={(e) => onChange("registrationDate", e.target.value)}
            style={inputStyle}
          />
        </Field>



        <Field label="Ｃｕｐｐｙのみ">
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      borderRadius: 10,
      border: "1px solid #e2e8f0",
      background: "#fff",
      fontWeight: 900,
      cursor: "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={form.cuppyOnly}
      onChange={(e) => onChange("cuppyOnly", e.target.checked)}
      style={{ width: 18, height: 18 }}
    />
    <span>Ｃｕｐｐｙのみ（書類アップロード必須を外す）</span>
  </label>
</Field>

        <Field label="業種（必須）">
          <select
            value={form.industry}
            onChange={(e) => {
              const value = e.target.value;
              const needsOther = value === "異業種" || value === "その他";

              setForm((prev) => ({
                ...prev,
                industry: value,
                industryOther: needsOther ? prev.industryOther : "",
              }));
            }}
            style={inputStyle}
          >
            <option value="美容医療">美容医療</option>
            <option value="エステティック">エステティック</option>
            <option value="BtoB">BtoB</option>
            <option value="異業種">異業種</option>
            <option value="その他">その他</option>
          </select>
          <ErrorText msg={errors.industry} />
        </Field>

        {(form.industry === "異業種" || form.industry === "その他") && (
          <Field label="業種（自由入力）">
            <input
              value={form.industryOther}
              onChange={(e) => onChange("industryOther", e.target.value)}
              placeholder="例：ITサービス、製造業 など"
              style={inputStyle}
            />
            <ErrorText msg={errors.industryOther} />
          </Field>
        )}

        <Field label="加盟店担当者名（必須）">
          <input
            value={form.contactName}
            onChange={(e) => onChange("contactName", e.target.value)}
            placeholder="例：山田 太郎"
            style={inputStyle}
          />
          <ErrorText msg={errors.contactName} />
        </Field>

        <Field label="加盟店担当者メール（必須）">
          <input
            value={form.contactEmail}
            onChange={(e) => onChange("contactEmail", e.target.value)}
            placeholder="example@merchant.co.jp"
            style={inputStyle}
          />
          <ErrorText msg={errors.contactEmail} />
        </Field>

        <Field label="加盟店担当者電話番号">
          <input
            value={form.contactPhone}
            onChange={(e) => onChange("contactPhone", formatJapanesePhone(e.target.value))}
            placeholder="例：090-xxxx-xxxx"
            style={inputStyle}
          />
        </Field>

        <Field label="備考">
          <input
            value={form.notes}
            onChange={(e) => onChange("notes", e.target.value)}
            placeholder="補足があれば入力"
            style={inputStyle}
          />
        </Field>
      </div>

      {/* ✅ NEW: 加盟店担当者（追加分） */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>加盟店担当者（追加）</div>

        <div style={box}>
          {errors.merchantContacts && (
            <div style={{ marginBottom: 10 }}>
              <ErrorText msg={errors.merchantContacts} />
            </div>
          )}

          {merchantContacts.map((c, idx) => (
            <div key={idx} style={row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                  担当者名
                </div>
                <input
                  value={c.name}
                  onChange={(e) => setMerchantContact(idx, "name", e.target.value)}
                  placeholder="例：佐藤 花子"
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
                <ErrorText msg={errors[`merchantContacts_${idx}_name`]} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                  メール
                </div>
                <input
                  value={c.email}
                  onChange={(e) => setMerchantContact(idx, "email", e.target.value)}
                  placeholder="例：sub@merchant.co.jp"
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
                <ErrorText msg={errors[`merchantContacts_${idx}_email`]} />
              </div>

              <div style={{ width: 120, display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => removeMerchantContact(idx)}
                  disabled={merchantContacts.length === 1}
                  style={{
                    ...dangerMini,
                    opacity: merchantContacts.length === 1 ? 0.5 : 1,
                    cursor: merchantContacts.length === 1 ? "not-allowed" : "pointer",
                  }}
                  title={merchantContacts.length === 1 ? "最低1行は表示されます" : "この行を削除"}
                >
                  削除
                </button>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-start" }}>
            <button type="button" onClick={addMerchantContact} style={miniBtn}>
              ＋ 追加
            </button>
          </div>
        </div>
      </div>

      {/* ✅ JMC担当者（複数） */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>JMC担当者</div>

        <div style={box}>
          {errors.jmcAssignees && (
            <div style={{ marginBottom: 10 }}>
              <ErrorText msg={errors.jmcAssignees} />
            </div>
          )}

          {jmcAssignees.map((a, idx) => (
            <div key={idx} style={row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                  担当者名（必須）
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    value={a.name}
                    onChange={(e) => setAssignee(idx, "name", e.target.value)}
                    placeholder="例：安成"
                    style={{ ...inputStyle, marginBottom: 0 }}
                  />

                  <select
  value=""
  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (!v) return;

    const picked = presetJmcAssignees.find((p) => p.name === v);
    setAssignee(idx, "name", v);

    // ✅ Auto-fill email if available (user can still overwrite manually)
    if (picked?.email) {
      setAssignee(idx, "email", picked.email);
    }
  }}
  style={{
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    width: 180,
  }}
  title="候補から選択（任意）"
>
  <option value="">候補から選択（任意）</option>
  {presetJmcAssignees.map((p) => (
    <option key={p.name} value={p.name}>
      {p.name}
    </option>
  ))}
</select>

                </div>

                <ErrorText msg={errors[`jmcAssignees_${idx}_name`]} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>
                  メール（必須）
                </div>
                <input
                  value={a.email}
                  onChange={(e) => setAssignee(idx, "email", e.target.value)}
                  placeholder="例：tanto@jmc.co.jp"
                  style={{ ...inputStyle, marginBottom: 0 }}
                />
                <ErrorText msg={errors[`jmcAssignees_${idx}_email`]} />
              </div>

              <div style={{ width: 120, display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => removeAssignee(idx)}
                  disabled={jmcAssignees.length === 1}
                  style={{
                    ...dangerMini,
                    opacity: jmcAssignees.length === 1 ? 0.5 : 1,
                    cursor: jmcAssignees.length === 1 ? "not-allowed" : "pointer",
                  }}
                  title={jmcAssignees.length === 1 ? "最低1名は必要です" : "この行を削除"}
                >
                  削除
                </button>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-start" }}>
            <button type="button" onClick={addAssignee} style={miniBtn}>
              ＋ 追加
            </button>
          </div>
        </div>
      </div>

      {apiError && (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#9f1239",
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          {apiError}
        </div>
      )}

      {result && (
        <div
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: 14,
            background: "#ecfeff",
            border: "1px solid #a5f3fc",
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>アカウント発行が完了しました</div>
          <div style={{ fontSize: 14, lineHeight: 1.9 }}>
            <div>
              <b>加盟店ID：</b> {result.merchant_id}
            </div>
            <div>
              <b>仮パスワード：</b> {result.temp_password}
            </div>
          </div>
          {/* ✅ Email status (green/red) */}
{result.email_sent ? (
  <div style={{ marginTop: 10, fontSize: 13, color: "#15803d", fontWeight: 900 }}>
    ✅ メール送信：成功（加盟店へ送信済み）
  </div>
) : (
  <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c", fontWeight: 900 }}>
    ❌ メール送信：失敗（手動で共有してください）
    {result.email_error ? (
      <div style={{ marginTop: 6, fontSize: 12, fontWeight: 800, color: "#b91c1c" }}>
        エラー：{result.email_error}
      </div>
    ) : null}
  </div>
)}

        </div>
      )}

      <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
        <button
          type="button"
          style={{
            ...primaryBtn,
            opacity: saving ? 0.6 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          onClick={handleCreate}
          disabled={saving}
        >
          {saving ? "保存中..." : "アカウント発行（仮）"}
        </button>

        <button type="button" style={secondaryBtn} onClick={resetAll}>
          クリア
        </button>
      </div>

      <div style={{ marginTop: 12, fontSize: 13, color: "#475569" }}>
        ※ 次のステップで「加盟店ID/仮パスワード生成」＋「メール送信」＋「DB保存」を実装します。
      </div>
    </StaffLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function ErrorText({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c", fontWeight: 800 }}>
      {msg}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e2e8f0",
  outline: "none",
  fontSize: 14,
  background: "#fff",
};

const primaryBtn: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "none",
  cursor: "pointer",
  background: "#0b1f3b",
  color: "#fff",
  fontWeight: 900,
};

const secondaryBtn: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  cursor: "pointer",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 800,
};

const box: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 12,
  background: "#fff",
};

const row: React.CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
  padding: 12,
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  marginBottom: 10,
};

const miniBtn: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 900,
  cursor: "pointer",
};

const dangerMini: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #fecaca",
  background: "#fff",
  color: "#b91c1c",
  fontWeight: 900,
};
